import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { articles, getArticleBySlug } from '@/mocks/articles';
import { useEffect } from 'react';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://pandabuy.my';

export default function DynamicArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const articleJsonLd = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${siteUrl}/article/${article.slug}#article`,
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    dateModified: '2026-06-29',
    author: {
      '@type': 'Organization',
      name: 'PandabuySheet',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'PandabuySheet',
      url: siteUrl,
    },
    image: {
      '@type': 'ImageObject',
      url: article.imageUrl,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/article/${article.slug}`,
    },
  } : null;

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <section className="pt-32 pb-20 px-4 text-center">
          <h1 className="font-heading text-3xl text-foreground-950 mb-4">Article Not Found</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">Back to Home</Link>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-50">
      {articleJsonLd && <JsonLd data={articleJsonLd} />}
      <Navbar />

      {/* Article Hero */}
      <section className="pt-28 pb-10 md:pt-32 md:pb-14 px-4 md:px-6 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Link
              to="/"
              className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
            <span className="text-foreground-300">/</span>
            <span className="text-sm text-foreground-600">Article</span>
          </div>
          <span className="inline-block px-3 py-1 bg-accent-100 text-accent-800 text-xs font-medium rounded-full mb-4">
            {article.readTime}
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-foreground-950 leading-[1.12] mb-4">
            {article.title}
          </h1>
          <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-4">
            Published: {article.date} &middot; {article.readTime}
          </p>
          <div className="relative w-full h-[240px] md:h-[360px] rounded-2xl overflow-hidden mb-0">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-10 md:py-14 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="prose-custom">
            {article.content.map((block, idx) => {
              if (block.type === 'paragraph' && block.text) {
                return (
                  <p key={idx} className="text-base md:text-lg text-foreground-700 leading-relaxed mb-8">
                    {block.text}
                  </p>
                );
              }
              if (block.type === 'heading' && block.text) {
                return (
                  <h2 key={idx} className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">
                    {block.text}
                  </h2>
                );
              }
              if (block.type === 'list' && block.items) {
                return (
                  <ul key={idx} className="space-y-3 mb-6 ml-1">
                    {block.items.map((item, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center mt-0.5">
                          <i className="ri-check-line text-accent-700 text-xs"></i>
                        </span>
                        <div>
                          <span className="text-sm font-semibold text-foreground-900">{item.title}</span>
                          <p className="text-sm text-foreground-500 mt-0.5">{item.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                );
              }
              if (block.type === 'table' && block.headers && block.rows) {
                return (
                  <div key={idx} className="overflow-x-auto mb-8 rounded-xl border border-background-200/70">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-background-100">
                          {block.headers.map((h, i) => (
                            <th key={i} className="text-left px-4 py-3 text-foreground-800 font-semibold">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {block.rows.map((row, i) => (
                          <tr key={i} className="border-t border-background-200/40">
                            {row.map((cell, j) => (
                              <td key={j} className={`px-4 py-3 ${j === 0 ? 'text-foreground-900 font-medium' : 'text-foreground-600'}`}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              }
              if (block.type === 'faq' && block.items) {
                return (
                  <div key={idx} className="mt-12 p-6 md:p-8 bg-background-100 rounded-2xl border border-background-200/40">
                    <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">Frequently Asked Questions</h2>
                    <div className="space-y-5">
                      {block.items.map((faq, i) => (
                        <div key={i} className="pb-5 border-b border-background-200/40 last:border-0 last:pb-0">
                          <h3 className="text-sm font-semibold text-foreground-900 mb-2">{faq.q}</h3>
                          <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              if (block.type === 'cta' && block.text) {
                return (
                  <div key={idx} className="mt-12 p-8 text-center bg-primary-500 rounded-2xl">
                    <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-4">Ready to Start Shopping Smarter?</h2>
                    <p className="text-sm md:text-base text-background-50/85 mb-6 max-w-lg mx-auto leading-relaxed">{block.text}</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <a
                        href="https://kaspreadsheet.com"
                        target="_blank"
                        rel="noopener nofollow"
                        className="inline-flex items-center gap-2 px-7 py-3 bg-background-50 text-foreground-950 rounded-full text-sm font-semibold hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
                      >
                        View Pandabuy Sheet
                        <i className="ri-arrow-right-up-line"></i>
                      </a>
                      <Link
                        to="/"
                        className="inline-flex items-center gap-2 px-7 py-3 bg-background-50/20 text-background-50 rounded-full text-sm font-semibold hover:bg-background-50/30 transition-all cursor-pointer whitespace-nowrap border border-background-50/30"
                      >
                        Back to Homepage
                        <i className="ri-home-line"></i>
                      </Link>
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      </article>

      {/* Related Articles */}
      <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-8">More Pandabuy Spreadsheet Guides</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {articles.filter(a => a.slug !== slug).slice(0, 3).map((a) => (
              <Link
                key={a.slug}
                to={`/article/${a.slug}`}
                className="group block bg-background-50 rounded-2xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all cursor-pointer"
              >
                <div className="w-full h-40 overflow-hidden">
                  <img src={a.imageUrl} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-4">
                  <span className="text-xs text-foreground-400">{a.date} &middot; {a.readTime}</span>
                  <h3 className="font-heading text-base text-foreground-950 mt-1.5 mb-1 line-clamp-2 leading-snug">{a.title}</h3>
                  <p className="text-xs text-foreground-500 line-clamp-2">{a.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}